import React, { useMemo } from 'react';
import { alpha, Box, Button, Chip, Paper, Stack, Tooltip } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import MultiSelectFilterButton from './filters/MultiSelectFilterButton';
import InvoicingPeriodFilter from './filters/InvoicingPeriodFilter';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { MIS_FILTER_KEYS, MIS_UI } from '../utils/misConstants';

const optionLabel = (options = [], id) => options.find((option) => option.id === id)?.label ?? id;

const FilterPill = ({ label, onClear }) => (
  <Chip
    size="small"
    label={label}
    onDelete={onClear}
    deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
    sx={{
      height: 28,
      fontSize: 12,
      fontWeight: 500,
      bgcolor: alpha('#4f5efa', 0.1),
      color: 'primary.main',
      border: 1,
      borderColor: alpha('#4f5efa', 0.2),
      '& .MuiChip-deleteIcon': {
        color: 'primary.main',
        '&:hover': { color: 'error.main' },
      },
    }}
  />
);

const MisDashboardFilterBar = ({ filters, setFilter, patchFilters, clearAll, filterOptions, loading = false }) => {
  const { misDashboard: m } = useTranslatedMessages();

  const activeFilters = useMemo(() => {
    const pills = [];
    const f = m.filters ?? {};

    (filters.businessProfileKeys ?? []).forEach((id) => {
      pills.push({
        key: `tin-${id}`,
        label: (f.pill_tin ?? 'TIN: {{value}}').replace('{{value}}', optionLabel(filterOptions?.business_profiles, id)),
        clear: () =>
          setFilter(
            MIS_FILTER_KEYS.BUSINESS_PROFILE_KEYS,
            (filters.businessProfileKeys ?? []).filter((item) => item !== id),
          ),
      });
    });

    (filters.documentKinds ?? []).forEach((id) => {
      pills.push({
        key: `doc-${id}`,
        label: (f.pill_doc ?? 'Doc: {{value}}').replace('{{value}}', optionLabel(filterOptions?.document_types, id)),
        clear: () =>
          setFilter(
            MIS_FILTER_KEYS.DOCUMENT_KINDS,
            (filters.documentKinds ?? []).filter((item) => item !== id),
          ),
      });
    });

    (filters.statuses ?? []).forEach((id) => {
      pills.push({
        key: `status-${id}`,
        label: (f.pill_status ?? 'Status: {{value}}').replace('{{value}}', optionLabel(filterOptions?.statuses, id)),
        clear: () =>
          setFilter(
            MIS_FILTER_KEYS.STATUSES,
            (filters.statuses ?? []).filter((item) => item !== id),
          ),
      });
    });

    (filters.vatCategoryCodes ?? []).forEach((id) => {
      pills.push({
        key: `vat-${id}`,
        label: (f.pill_vat ?? 'VAT: {{value}}').replace('{{value}}', optionLabel(filterOptions?.vat_categories, id)),
        clear: () =>
          setFilter(
            MIS_FILTER_KEYS.VAT_CATEGORY_CODES,
            (filters.vatCategoryCodes ?? []).filter((item) => item !== id),
          ),
      });
    });

    (filters.customerPartyKeys ?? []).forEach((id) => {
      pills.push({
        key: `customer-${id}`,
        label: (f.pill_customer ?? 'Customer: {{value}}').replace(
          '{{value}}',
          optionLabel(filterOptions?.customers, id),
        ),
        clear: () =>
          setFilter(
            MIS_FILTER_KEYS.CUSTOMER_PARTY_KEYS,
            (filters.customerPartyKeys ?? []).filter((item) => item !== id),
          ),
      });
    });

    return pills;
  }, [filters, filterOptions, m.filters, setFilter]);

  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, bgcolor: MIS_UI.SURFACE_WHITE, borderColor: MIS_UI.BORDER }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <FilterListIcon fontSize="small" color="action" sx={{ mr: 0.5, flexShrink: 0 }} />

        <Tooltip title={m.filters?.coming_soon ?? 'Coming soon'} arrow>
          <span>
            <MultiSelectFilterButton
              label={m.filters?.vat_entity ?? 'VAT Entity'}
              options={filterOptions?.vat_entities ?? []}
              value={[]}
              onChange={() => {}}
              disabled
              searchable
            />
          </span>
        </Tooltip>

        <MultiSelectFilterButton
          label={m.filters?.tin_business ?? 'TIN / Business'}
          options={filterOptions?.business_profiles ?? []}
          value={filters.businessProfileKeys}
          onChange={(value) => setFilter(MIS_FILTER_KEYS.BUSINESS_PROFILE_KEYS, value)}
          disabled={loading}
          searchable
        />
        <MultiSelectFilterButton
          label={m.filters?.doc_type ?? 'Doc Type'}
          options={filterOptions?.document_types ?? []}
          value={filters.documentKinds}
          onChange={(value) => setFilter(MIS_FILTER_KEYS.DOCUMENT_KINDS, value)}
          disabled={loading}
        />
        <MultiSelectFilterButton
          label={m.filters?.status ?? 'Status'}
          options={filterOptions?.statuses ?? []}
          value={filters.statuses}
          onChange={(value) => setFilter(MIS_FILTER_KEYS.STATUSES, value)}
          disabled={loading}
        />
        <MultiSelectFilterButton
          label={m.filters?.vat_category ?? 'VAT Category'}
          options={filterOptions?.vat_categories ?? []}
          value={filters.vatCategoryCodes}
          onChange={(value) => setFilter(MIS_FILTER_KEYS.VAT_CATEGORY_CODES, value)}
          disabled={loading}
        />
        <MultiSelectFilterButton
          label={m.filters?.customer ?? 'Customer'}
          options={filterOptions?.customers ?? []}
          value={filters.customerPartyKeys}
          onChange={(value) => setFilter(MIS_FILTER_KEYS.CUSTOMER_PARTY_KEYS, value)}
          disabled={loading}
          searchable
        />
        <InvoicingPeriodFilter
          startDate={filters.startDate}
          endDate={filters.endDate}
          periodPreset={filters.periodPreset}
          onChange={({ startDate, endDate, periodPreset }) => {
            patchFilters({
              [MIS_FILTER_KEYS.START_DATE]: startDate,
              [MIS_FILTER_KEYS.END_DATE]: endDate,
              [MIS_FILTER_KEYS.PERIOD_PRESET]: periodPreset,
            });
          }}
          disabled={loading}
        />

        {activeFilters.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={clearAll}
            sx={{
              height: 32,
              textTransform: 'none',
              fontSize: 12,
              color: 'text.secondary',
              '&:hover': { color: 'error.main' },
            }}
          >
            {m.filters?.clear_all ?? 'Clear All'}
          </Button>
        )}
      </Stack>

      {activeFilters.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {activeFilters.map((pill) => (
            <FilterPill key={pill.key} label={pill.label} onClear={pill.clear} />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default MisDashboardFilterBar;
